import * as ApolloV3 from '@apollo/client';
import * as ApolloReactV2 from '@apollo/react-hooks';
import {InMemoryCache as InMemoryCacheV2} from 'apollo-cache-inmemory';
import {
  ApolloError as ApolloErrorV2,
  NetworkStatus as NetworkStatusV2,
} from 'apollo-client';

import {isApolloClientV3Enabled} from '../../apolloRuntime';

// Keep one app-wide import surface while the startup flag selects a coherent
// provider/hook/client stack for the lifetime of the page.
export {
  type ApolloClient,
  type ApolloQueryResult,
  type LazyQueryHookOptions,
  type MutationHookOptions,
  type NormalizedCacheObject,
  type Operation,
  type OperationVariables,
  type QueryHookOptions,
  type QueryLazyOptions,
  type QueryResult,
  type SkipToken,
  skipToken,
  type SuspenseQueryHookOptions,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from '@apollo/client';
export type {ExecutionResult} from 'graphql';

export const ApolloProvider = (
  isApolloClientV3Enabled
    ? ApolloV3.ApolloProvider
    : ApolloReactV2.ApolloProvider
) as typeof ApolloV3.ApolloProvider;
export const useApolloClient = (
  isApolloClientV3Enabled
    ? ApolloV3.useApolloClient
    : ApolloReactV2.useApolloClient
) as typeof ApolloV3.useApolloClient;
export const useQuery = (
  isApolloClientV3Enabled ? ApolloV3.useQuery : ApolloReactV2.useQuery
) as typeof ApolloV3.useQuery;
export const useLazyQuery = (
  isApolloClientV3Enabled ? ApolloV3.useLazyQuery : ApolloReactV2.useLazyQuery
) as typeof ApolloV3.useLazyQuery;
export const useMutation = (
  isApolloClientV3Enabled ? ApolloV3.useMutation : ApolloReactV2.useMutation
) as typeof ApolloV3.useMutation;
export const ApolloError = (
  isApolloClientV3Enabled ? ApolloV3.ApolloError : ApolloErrorV2
) as typeof ApolloV3.ApolloError;
export type ApolloError = ApolloV3.ApolloError;
export const NetworkStatus = (
  isApolloClientV3Enabled ? ApolloV3.NetworkStatus : NetworkStatusV2
) as typeof ApolloV3.NetworkStatus;
export type NetworkStatus = ApolloV3.NetworkStatus;
export const InMemoryCache = (
  isApolloClientV3Enabled ? ApolloV3.InMemoryCache : InMemoryCacheV2
) as typeof ApolloV3.InMemoryCache;

// As of Feb 2024, query dedupe is much more impactful from a performance perspective than query abort.
// We're disabling query dedupe for now, but we may want to re-enable it in the future.
// It must be disabled in order to enable Apollo's internal query dedupe, since aborting inflight queries
// with query dedupe enabled causes Apollo to never fire the query again, even on remount.

// We wrap Apollo's `useQuery` in logic which aborts the query when the component unmounts.
// Apollo uses `fetch` under the hood, which is cancellable via `AbortController`.
// See https://developer.mozilla.org/en-US/docs/Web/API/AbortController.
// export const useQuery: typeof useApolloQuery = (document, options = {}) => {
//   // This `useMemo` is required so we only create one `AbortController` on mount.
//   const controller = useMemo(() => new AbortController(), []);
//   // This only runs once on component unmount.
//   useEffect(() => () => controller.abort(), [controller]);

//   // Preserve passed in options while populating `options.context.fetchOptions.signal`.
//   const optionsWithSignal = produce(options, draft => {
//     draft.context = draft.context ?? {};
//     draft.context.fetchOptions = draft.context.fetchOptions ?? {};
//     draft.context.fetchOptions.signal = controller.signal;
//   });

//   return useApolloQuery(document, optionsWithSignal);
// };
